// The submit-queue session endpoints, in one place.
//
// Instance A owns the routes (backend/app/routes/queue.py); the contract is
// docs/submit-queue.md, "Session API" (v0.1). Every path is
// practice-scoped, and every field name the queue UI reads is named in this file
// and nowhere else, so reconciling with A's published JSON is an edit here rather
// than a hunt through four components. CSRF is attached by the wrapper index.js
// installs on window.fetch, so a plain fetch is correct.
//
// PHI: nothing this module sends or reads for a LIST carries a patient name. The
// queue list is claim id and DOS; the name stays in the ledger's claim detail.

import { filenameFrom } from "../reports/exportDownload";

const API_URL = process.env.REACT_APP_API_URL || "";

// The claim statuses A's `submission_queue_service.enqueue` accepts. Kept in step
// with the server deliberately rather than derived: the server is authoritative
// and still refuses, this only decides whether the button is offered at all.
export const ENQUEUEABLE_STATUSES = ["draft", "rejected", "error"];

// Never surface a server message we did not write. A returns {error: "..."} for
// the refusals a biller can act on (rules, already queued) and a generic sentence
// for anything else.
async function readError(response, fallback) {
  try {
    const body = await response.json();
    return { message: (body && body.error) || fallback, body: body || {} };
  } catch (err) {
    return { message: fallback, body: {} };
  }
}

export async function request(url, options, fallback) {
  const response = await fetch(url, { credentials: "include", ...options });
  if (!response.ok) {
    const { message, body } = await readError(response, fallback);
    const error = new Error(message);
    error.status = response.status;
    error.body = body;
    throw error;
  }
  // A 200 is not a payload. A truncated body or a proxy's HTML page parses to
  // null, and a caller defaulting that to [] would render "the queue is empty" at
  // exactly the moment we could not check.
  const body = await response.json().catch(() => null);
  if (!body || typeof body !== "object") {
    const error = new Error(
      "The response could not be read, so this may be incomplete. Try again.",
    );
    error.status = response.status;
    error.body = {};
    throw error;
  }
  return body;
}

export function post(payload) {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  };
}

const base = (practiceId) => `${API_URL}/api/practices/${practiceId}/queue`;

// GET …/queue → {summary, items}, normalised. Refuses a body without an items
// array rather than rendering an all-clear.
export async function fetchQueue(practiceId) {
  const body = await request(base(practiceId), {}, "Could not load the queue.");
  if (!Array.isArray(body.items)) {
    throw new Error(
      "The response could not be read, so this list may be incomplete. Try again.",
    );
  }
  return {
    summary: normalizeSummary(body.summary),
    items: body.items.map(normalizeItem),
  };
}

// `{item, portal}` — the claim's live queue row (queued, leased or held), or null,
// plus the practice's submit portal. For the Submit-to-queue button on the ledger's
// claim row and in the claim editor. A's single-claim read (contract 0.2): no summary,
// no reconciliation, a lease sweep and a PHI access-log entry for this one claim only.
// It is a hint for the button, not a guard — the server's 409 still refuses a second
// enqueue.
//
// `portal` rides on THIS response rather than being fetched separately because the
// button is mounted on every row of the ledger: a second request per row to learn one
// per-practice string would be a page-load's worth of requests for a label.
export async function fetchClaimLiveItem(practiceId, claimId) {
  const body = await request(
    `${base(practiceId)}?claim_id=${encodeURIComponent(claimId)}`,
    {},
    "Could not load this claim's queue state.",
  );
  if (!Array.isArray(body.items)) {
    throw new Error("The queue response could not be read.");
  }
  // Oldest first; at most one row is live (the partial unique index).
  const live = body.items
    .map(normalizeItem)
    .filter((item) => ["queued", "leased", "held"].includes(item.status));
  return {
    item: live.length ? live[live.length - 1] : null,
    portal: body.portal || null,
  };
}

// POST …/items/<id>/not-sent {reason} → {item}. A held row a person checked and
// found did not go: back to queued, reason left on the claim. 409 unless held.
export function markNotSent(practiceId, itemId, reason) {
  return request(
    `${base(practiceId)}/items/${itemId}/not-sent`,
    post({ reason }),
    "Could not return this claim to the queue.",
  );
}

// POST …/queue/items {claim_id}. 422 carries the rules reasons, 409 means a live
// row already exists; both come back on `error.body` for the button to render.
export function enqueueClaim(practiceId, claimId) {
  return request(
    `${base(practiceId)}/items`,
    post({ claim_id: claimId }),
    "Could not add this claim to the queue.",
  );
}

export function removeItem(practiceId, itemId, reason) {
  return request(
    `${base(practiceId)}/items/${itemId}/remove`,
    post({ reason }),
    "Could not remove this claim from the queue.",
  );
}

// POST …/items/<id>/submitted {confirmation_ref, channel, portal?}. Never
// optimistic: the server refuses a live lease, a finished row, a reference already
// recorded for the claim, and (for Claim.MD through the portal) anything that is
// not Claim.MD's claim id.
export function markSubmitted(
  practiceId,
  itemId,
  { confirmationRef, channel, portal },
) {
  const body = { confirmation_ref: confirmationRef, channel };
  if (portal) body.portal = portal;
  return request(
    `${base(practiceId)}/items/${itemId}/submitted`,
    post(body),
    "Could not record this submission.",
  );
}

// GET …/runbook[?portal=] → {portal, portals, latest, history}, normalised.
export async function fetchRunbook(practiceId, portal) {
  const qs = portal ? `?portal=${encodeURIComponent(portal)}` : "";
  const body = await request(
    `${base(practiceId)}/runbook${qs}`,
    {},
    "Could not load the runbook.",
  );
  if (!Array.isArray(body.portals) || !Array.isArray(body.history)) {
    throw new Error(
      "The response could not be read, so the runbook may be incomplete. Try again.",
    );
  }
  return {
    portal: body.portal || null,
    portals: body.portals,
    latest: body.latest ? normalizeRunbook(body.latest) : null,
    history: body.history.map(normalizeRunbook),
  };
}

// POST …/runbook — always a NEW version; the server never takes approval from
// the body, and the new version is unapproved.
export async function saveRunbookVersion(
  practiceId,
  { portal, prose, structured, submitChannel },
) {
  const body = await request(
    `${base(practiceId)}/runbook`,
    post({
      portal,
      prose,
      structured,
      submit_channel: submitChannel || null,
    }),
    "Could not save the runbook.",
  );
  return body.runbook ? normalizeRunbook(body.runbook) : null;
}

export async function approveRunbook(practiceId, runbookId) {
  const body = await request(
    `${base(practiceId)}/runbook/${runbookId}/approve`,
    post({}),
    "Could not approve the runbook.",
  );
  return body.runbook ? normalizeRunbook(body.runbook) : null;
}

// POST …/runbook/<id>/unapprove — an admin withdraws an approval. Idempotent.
export async function unapproveRunbook(practiceId, runbookId) {
  const body = await request(
    `${base(practiceId)}/runbook/${runbookId}/unapprove`,
    post({}),
    "Could not withdraw the approval.",
  );
  return body.runbook ? normalizeRunbook(body.runbook) : null;
}

// POST …/runbook/upload — a runbook FILE (v1/v8 or v2) as the next version, through
// the same append the CLI's `post` reaches. Multipart, no JSON header: the browser
// sets the boundary. A 422 carries `problems` (the list the page shows) on
// `error.body`; `error.problems` is the same list for convenience.
export async function uploadRunbook(
  practiceId,
  file,
  { portal, submitChannel } = {},
) {
  const form = new FormData();
  form.append("file", file, file.name || "runbook.md");
  if (portal) form.append("portal", portal);
  if (submitChannel) form.append("submit_channel", submitChannel);
  try {
    const body = await request(
      `${base(practiceId)}/runbook/upload`,
      { method: "POST", body: form },
      "Could not upload the runbook.",
    );
    return body.runbook ? normalizeRunbook(body.runbook) : null;
  } catch (err) {
    err.problems = Array.isArray(err.body && err.body.problems)
      ? err.body.problems
      : [];
    throw err;
  }
}

// GET …/queue/coverage → {coverage}, normalised. `applicable: false` is content (the
// practice has no submit portal, or submits through its clearinghouse) and every gate
// is open; the page says why there is nothing to show.
export async function fetchCoverage(practiceId) {
  const body = await request(
    `${base(practiceId)}/coverage`,
    {},
    "Could not load the coverage matrix.",
  );
  if (!body.coverage || typeof body.coverage !== "object") {
    throw new Error("The coverage matrix could not be read.");
  }
  return normalizeCoverage(body.coverage);
}

// GET …/queue/sign-in → {sign_in}. Whether the last run stopped at the portal's
// sign-in, the runner's sentence, where to sign in, how many items wait.
export async function fetchSignInState(practiceId) {
  const body = await request(
    `${base(practiceId)}/sign-in`,
    {},
    "Could not load the sign-in state.",
  );
  if (!body.sign_in || typeof body.sign_in !== "object") {
    throw new Error("The sign-in state could not be read.");
  }
  return normalizeSignIn(body.sign_in);
}

// POST …/queue/sign-in/retry → {retry, sign_in}. Returns the stopped run's leases to
// the queue so the next run takes them; 409 when nothing is waiting.
export async function retrySignIn(practiceId) {
  const body = await request(
    `${base(practiceId)}/sign-in/retry`,
    post({}),
    "Could not retry after the sign-in.",
  );
  return {
    released: num(body.retry && body.retry.released) ?? 0,
    pending: num(body.retry && body.retry.pending) ?? 0,
    signIn: normalizeSignIn(body.sign_in),
  };
}

// GET …/runs → {runs: [RUN]}, the last 20, newest first.
export async function fetchRuns(practiceId) {
  const body = await request(
    `${base(practiceId)}/runs`,
    {},
    "Could not load the skill runs.",
  );
  if (!Array.isArray(body.runs)) {
    throw new Error(
      "The response could not be read, so this list may be incomplete. Try again.",
    );
  }
  return body.runs.map(normalizeRun);
}

// GET …/queue/drain-status → {drain}. "How deep is this queue, who drained it last,
// and when was it last empty." Counts and timestamps only — no claim ids, no patient
// fields — so it is a separate, cheap read from the queue list beside it.
export async function fetchDrainStatus(practiceId) {
  const body = await request(
    `${base(practiceId)}/drain-status`,
    {},
    "Could not load the drain status.",
  );
  if (!body.drain || typeof body.drain !== "object") {
    throw new Error("The drain status could not be read.");
  }
  return normalizeDrain(body.drain);
}

// GET /api/practices/<id>/submit-skill → {bundle}. Admin only; a non-admin gets 403
// and the panel is not rendered for them anyway.
export async function fetchSkillBundle(practiceId) {
  const body = await request(
    `${API_URL}/api/practices/${practiceId}/submit-skill`,
    {},
    "Could not load the skill bundle.",
  );
  if (!body.bundle || typeof body.bundle !== "object") {
    throw new Error("The skill bundle could not be read.");
  }
  return normalizeBundle(body.bundle);
}

// The download is a FETCH, not an <a href>, and that is not a preference. Atlas
// authenticates /api/* with the Clerk session token `authFetch.js` attaches to fetch
// calls; a plain link navigation carries no Authorization header, so it would get a
// 401 in production while passing every test that logs in through the dead cookie
// path. `exportDownload.js` says the same thing in its own header, and every other
// download in the app goes through fetch -> blob. Caught in adversarial review,
// 2026-09-21; the first version of this was a link.
//
// `filenameFrom` is reused from the exports module rather than re-parsed here — one
// Content-Disposition parser. Straight from the module, not through `../reports`,
// so the two features do not start importing each other's index.
export async function downloadSkillBundle(practiceId) {
  const response = await fetch(
    `${API_URL}/api/practices/${practiceId}/submit-skill.zip`,
    { credentials: "include" },
  );
  if (!response.ok) {
    let message = "The bundle could not be downloaded.";
    try {
      const body = await response.json();
      if (body && body.error) message = body.error;
    } catch (err) {
      /* not JSON */
    }
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }
  const filename = filenameFrom(
    response.headers.get("Content-Disposition"),
    `atlas-submit-skill-practice-${practiceId}.zip`,
  );
  const blob = await response.blob();
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
  return filename;
}

// What a biller calls each portal. A key with no entry shows as itself rather than as
// a blank — an unfamiliar key is information, not an error.
const PORTAL_LABELS = {
  claimmd: "Claim.MD",
  practicefusion: "Practice Fusion",
  tebra: "Tebra",
  manual: "outside Atlas",
};

export function portalLabel(key) {
  if (!key) return null;
  return PORTAL_LABELS[key] || key;
}

// ---- Reconciliation (Instance D: routes/reconciliation.py, docs/reconciliation.md) ----

const reconBase = (practiceId) =>
  `${API_URL}/api/practices/${practiceId}/reconciliation`;

export const MISMATCH_KINDS = ["sent_unacknowledged", "seen_unrecorded"];

// GET …/reconciliation/mismatches. A 404 means there is no feed to show (D's routes
// not deployed, or nothing registered) and resolves to `null` so the panel can say
// so; every other failure throws like any other read.
export async function fetchMismatches(practiceId) {
  let body;
  try {
    body = await request(
      `${reconBase(practiceId)}/mismatches`,
      {},
      "Could not load reconciliation.",
    );
  } catch (err) {
    if (err.status === 404) return null;
    throw err;
  }
  const lists = ["sent_unacknowledged", "seen_unrecorded", "queued_stale"];
  if (lists.some((key) => !Array.isArray(body[key]))) {
    throw new Error(
      "The reconciliation response could not be read, so it may be incomplete. Try again.",
    );
  }
  const far = body.far_side || {};
  return {
    checkedAt: body.checked_at || null,
    recordingSince: body.recording_since || null,
    farSide: {
      key: far.key || null,
      lastRefreshedAt: far.last_refreshed_at || null,
      refreshable: far.refreshable === true,
    },
    sentUnacknowledged: body.sent_unacknowledged.map(normalizeMismatch),
    seenUnrecorded: body.seen_unrecorded.map(normalizeMismatch),
    queuedStale: body.queued_stale.map(normalizeMismatch),
    acknowledgedCount: num(body.acknowledged_count) ?? 0,
    thresholds:
      body.thresholds && typeof body.thresholds === "object"
        ? body.thresholds
        : {},
  };
}

// POST …/reconciliation/acknowledge {claim_id, kind, reason} → 201. Appends a
// comment on the claim; it never deletes or changes a submission.
export function acknowledgeMismatch(practiceId, { claimId, kind, reason }) {
  return request(
    `${reconBase(practiceId)}/acknowledge`,
    post({ claim_id: claimId, kind, reason }),
    "Could not acknowledge this mismatch.",
  );
}

// ---- Shape helpers. The only places a response field name is read. ----

// The queue row a write route returns, normalised. Every write answers {item: ITEM}.
export function itemFrom(body) {
  if (!body || typeof body !== "object") return null;
  if (body.item && typeof body.item === "object")
    return normalizeItem(body.item);
  return null;
}

// Enqueue's two 409s: {error, item_id, status} means a live row already exists;
// {error, claim_status} means the claim is in the wrong status. Only the first is
// "already queued".
export function isAlreadyQueued(body) {
  return !!body && body.item_id != null;
}

// What a 422 refused the enqueue for. Two shapes:
// - rules findings, `reasons`: FINDING is {rule_key, version, action_type, reason,
//   proposed?}; `reason` is the sentence a biller reads.
// - the 837 builder's refusal, `codes`: ["ZERO_CHARGE (service_lines[0].charge)", …],
//   the same list the claim editor's "As it will be sent" panel shows.
// An empty list means the engine could not evaluate the claim; the caller shows
// `error` instead.
export function reasonsFrom(body) {
  const raw = (body && (body.reasons || body.failures || body.codes)) || [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((r) => (typeof r === "string" ? r : r && (r.message || r.reason)))
    .filter(Boolean);
}

// Findings that did not block the enqueue, same FINDING shape, on the 201.
export function warningsFrom(body) {
  const raw = (body && body.warnings) || [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((w) => (typeof w === "string" ? w : w && (w.message || w.reason)))
    .filter(Boolean);
}

// Queue timestamps are UTC with a trailing "Z". Other Atlas routes send bare ISO
// strings, which a browser would read as local time, so a missing zone is still
// treated as UTC rather than trusted.
export function parseServerTime(value) {
  if (!value) return null;
  const text = String(value);
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(text.slice(10));
  const when = new Date(hasZone ? text : `${text}Z`);
  return Number.isNaN(when.getTime()) ? null : when;
}

const num = (value) => {
  const n = Number(value);
  return value == null || !Number.isFinite(n) ? null : n;
};

// A queue row as the UI reads it. Deliberately has no patient field: if A's row
// ever carries one, it is dropped here rather than rendered in a list.
export function normalizeItem(raw) {
  const row = raw || {};
  return {
    id: row.id,
    claimId: row.claim_id ?? null,
    dateOfService: row.date_of_service || null,
    payerName: row.payer_name || null,
    billedAmount: row.billed_amount ?? null,
    // Unknown, not "queued": a row whose status we cannot read must not be
    // offered the actions a queued row gets.
    status: row.status || "unknown",
    enqueuedBy: row.enqueued_by || null,
    enqueuedAt: row.enqueued_at || null,
    ageDays: num(row.age_days),
    // Can still name the last holder after a lease expired; only meaningful
    // alongside status "leased".
    leasedBy: row.leased_by || null,
    leasedAt: row.leased_at || null,
    leaseExpiresAt: row.lease_expires_at || null,
    submittedBy: row.submitted_by || null,
    submittedAt: row.submitted_at || null,
    submissionChannel: row.submission_channel || null,
    confirmationRef: row.confirmation_ref || null,
    removedBy: row.removed_by || null,
    removedAt: row.removed_at || null,
    removedReason: row.removed_reason || null,
    // For a held row, when it was held (A's hold writes it; D ages held rows by it).
    updatedAt: row.updated_at || null,
    claimStatus: row.claim_status || null,
  };
}

export function normalizeSummary(raw) {
  const s = raw || {};
  return {
    queued: num(s.queued) ?? 0,
    leased: num(s.leased) ?? 0,
    held: num(s.held) ?? 0,
    submittedToday: num(s.submitted_today) ?? 0,
    oldestQueuedDays: num(s.oldest_queued_days),
    lastRun: s.last_run || null,
    // null when D's count raised: "unknown", not zero.
    openMismatches: num(s.open_mismatches),
  };
}

// A lease is live while status is "leased" and lease_expires_at is in the future.
// The server returns expired leases to "queued" on its next read; between reads a
// row can still say "leased" with a past expiry, and the actions A allows after
// expiry (I submitted, remove) should not wait for a refresh.
export function leaseIsLive(item, now = new Date()) {
  if (!item || item.status !== "leased") return false;
  const expires = parseServerTime(item.leaseExpiresAt);
  // No expiry on a leased row: treat as live. Refusing locally is safer than
  // offering a button the server will 409.
  if (!expires) return true;
  return expires.getTime() > now.getTime();
}

// Rows a person may take off the queue: queued, or leased with the lease expired.
// Never held — A refuses remove on a held row (409), because a held claim may
// already have gone and only a person who checked the portal can say.
export function canRemove(item, now = new Date()) {
  if (!item) return false;
  if (item.status === "queued") return true;
  return item.status === "leased" && !leaseIsLive(item, now);
}

// Rows a person may record as sent: everything removable, plus held — "I
// submitted" with the portal's reference is how a held row is resolved.
export function isActionable(item, now = new Date()) {
  if (!item) return false;
  return item.status === "held" || canRemove(item, now);
}

export function normalizeRunbook(raw) {
  const r = raw || {};
  return {
    id: r.id,
    portalKey: r.portal_key || null,
    version: num(r.version),
    prose: typeof r.prose === "string" ? r.prose : "",
    // An object, or null. Anything else is shown as absent rather than guessed at.
    structured:
      r.structured &&
      typeof r.structured === "object" &&
      !Array.isArray(r.structured)
        ? r.structured
        : null,
    submitChannel: r.submit_channel || null,
    author: r.author || null,
    approvedBy: r.approved_by || null,
    approvedAt: r.approved_at || null,
    createdAt: r.created_at || null,
    // The server's reading of the version (brief 15): schema, UNKNOWN count, a v2's
    // coverage and live-readiness problems. null on a response that predates it.
    summary:
      r.summary && typeof r.summary === "object"
        ? normalizeSummary_(r.summary)
        : null,
  };
}

function normalizeSummary_(raw) {
  const s = raw || {};
  const cov = s.coverage && typeof s.coverage === "object" ? s.coverage : null;
  return {
    schema: s.schema || null,
    unknownCount: num(s.unknown_count) ?? 0,
    operations: num(s.operations),
    coverage: cov
      ? {
          available: num(cov.available) ?? 0,
          of: num(cov.of) ?? 0,
          reads: num(cov.reads) ?? 0,
          writes: num(cov.writes) ?? 0,
          releasable: num(cov.releasable) ?? 0,
          complete: Array.isArray(cov.complete) ? cov.complete : [],
          partial: Array.isArray(cov.partial) ? cov.partial : [],
          missing: Array.isArray(cov.missing) ? cov.missing : [],
        }
      : null,
    // true / false / null (not a v2, so the question does not arise)
    liveReady: typeof s.live_ready === "boolean" ? s.live_ready : null,
    problems: Array.isArray(s.problems) ? s.problems : [],
  };
}

// A gate: {allowed, state, needs, reason}. A gate the server did not send is OPEN —
// the gate is a courtesy ahead of the server's own refusal, never the boundary.
function normalizeGate(raw) {
  const g = raw && typeof raw === "object" ? raw : {};
  return {
    allowed: g.allowed !== false,
    state: g.state || "not_applicable",
    needs: Array.isArray(g.needs) ? g.needs : [],
    reason: g.reason || null,
  };
}

export const GATED_FEATURES = ["submit", "corrections", "payment_posting"];

export function normalizeGates(raw) {
  const g = raw && typeof raw === "object" ? raw : {};
  const out = { applicable: g.applicable === true, portal: g.portal || null };
  GATED_FEATURES.forEach((key) => {
    out[key] = normalizeGate(g[key]);
  });
  return out;
}

export function normalizeCoverage(raw) {
  const c = raw || {};
  const rb = c.runbook && typeof c.runbook === "object" ? c.runbook : null;
  return {
    applicable: c.applicable === true,
    reason: c.reason || null,
    portal: c.portal || null,
    portalLabel: c.portal_label || portalLabel(c.portal) || null,
    kitUrl: c.kit_url || null,
    signInUrl: c.sign_in_url || null,
    runbook: rb
      ? {
          id: rb.id ?? null,
          version: num(rb.version),
          author: rb.author || null,
          approvedBy: rb.approved_by || null,
          approvedAt: rb.approved_at || null,
          createdAt: rb.created_at || null,
          submitChannel: rb.submit_channel || null,
          summary: rb.summary ? normalizeSummary_(rb.summary) : null,
        }
      : null,
    operations: (Array.isArray(c.operations) ? c.operations : []).map((o) => ({
      key: o.key,
      kind: o.kind || null,
      optional: o.optional === true,
      description: o.description || "",
      state: o.state || "missing",
      reasons: Array.isArray(o.reasons) ? o.reasons : [],
      features: Array.isArray(o.features) ? o.features : [],
      requiredBy: Array.isArray(o.required_by) ? o.required_by : [],
    })),
    features: (Array.isArray(c.features) ? c.features : []).map((f) => ({
      key: f.key,
      label: f.label || f.key,
      description: f.description || "",
      status: f.status || "missing",
      required: Array.isArray(f.required) ? f.required : [],
      uses: Array.isArray(f.uses) ? f.uses : [],
      missing: Array.isArray(f.missing) ? f.missing : [],
      partial: Array.isArray(f.partial) ? f.partial : [],
      optionalMissing: Array.isArray(f.optional_missing)
        ? f.optional_missing
        : [],
      needs: Array.isArray(f.needs) ? f.needs : [],
      gated: f.gated === true,
    })),
    unavailable: (Array.isArray(c.unavailable) ? c.unavailable : []).map(
      (u) => ({
        feature: u.feature,
        label: u.label || u.feature,
        status: u.status || "missing",
        needs: Array.isArray(u.needs) ? u.needs : [],
        gated: u.gated === true,
      }),
    ),
    gates: normalizeGates(c.gates),
  };
}

export function normalizeSignIn(raw) {
  const s = raw || {};
  return {
    needsSignIn: s.needs_sign_in === true,
    runId: s.run_id ?? null,
    operator: s.operator || null,
    at: s.at || null,
    message: s.message || null,
    signInUrl: s.sign_in_url || null,
    portal: s.portal || null,
    portalLabel: s.portal_label || portalLabel(s.portal) || null,
    pending: num(s.pending) ?? 0,
  };
}

export function normalizeRun(raw) {
  const r = raw || {};
  return {
    id: r.id,
    skill: r.skill || null,
    startedAt: r.started_at || null,
    finishedAt: r.finished_at || null,
    status: r.status || "unknown",
    operator: r.operator || null,
    runbookId: r.runbook_id ?? null,
    claimedCount: num(r.claimed_count) ?? 0,
    submittedCount: num(r.submitted_count) ?? 0,
    failedCount: num(r.failed_count) ?? 0,
    needsHuman: r.needs_human === true,
    notes: r.notes || null,
  };
}

// The drain status. `lastEmptiedAt` is a DERIVATION on the server (the finish time of
// the last run after which the queue was empty), never a stored column, so it can move
// backwards if rows are removed — nothing here caches it.
export function normalizeDrain(raw) {
  const d = raw || {};
  const run = d.last_run || null;
  return {
    practiceId: d.practice_id ?? null,
    portal: d.portal || null,
    queueDepth: num(d.queue_depth) ?? 0,
    queued: num(d.queued) ?? 0,
    leased: num(d.leased) ?? 0,
    held: num(d.held) ?? 0,
    lastEmptiedAt: d.last_emptied_at || null,
    lastEmptiedRunId: d.last_emptied_run_id ?? null,
    lastRun: run
      ? {
          id: run.id ?? null,
          status: run.status || "unknown",
          operator: run.operator || null,
          startedAt: run.started_at || null,
          finishedAt: run.finished_at || null,
          claimedCount: num(run.claimed_count) ?? 0,
          submittedCount: num(run.submitted_count) ?? 0,
          failedCount: num(run.failed_count) ?? 0,
          // A FLAG, not a count: skill_runs records whether the run needed a person,
          // and nothing anywhere tallies how many claims did. `held` is the number.
          needsHuman: run.needs_human === true,
        }
      : null,
  };
}

// The skill bundle's availability. `available: false` is CONTENT — the reason and the
// sentence are what the panel renders — so nothing here treats it as an error.
export function normalizeBundle(raw) {
  const b = raw || {};
  const rb = b.runbook || null;
  const release = b.release || {};
  const discovery = b.discovery || {};
  return {
    available: b.available === true,
    portal: b.portal || null,
    reason: b.reason || null,
    message: b.message || null,
    version: num(b.version),
    scopes: Array.isArray(b.scopes) ? b.scopes : [],
    runbook: rb
      ? {
          id: rb.id ?? null,
          version: num(rb.version),
          submitChannel: rb.submit_channel || null,
          approvedBy: rb.approved_by || null,
          approvedAt: rb.approved_at || null,
          hasStructured: rb.has_structured === true,
        }
      : null,
    release: {
      repo: release.repo || null,
      url: release.url || null,
      kind: release.kind || null,
      ref: release.ref || null,
    },
    discovery: {
      section: discovery.section || null,
      url: discovery.url || null,
    },
  };
}

// A reconciliation entry: claim id, DOS, payer, a fixed sentence. D never sends a
// patient field; anything else on the entry is dropped here all the same.
export function normalizeMismatch(raw) {
  const e = raw || {};
  return {
    claimId: e.claim_id ?? null,
    dateOfService: e.dos || null,
    payerName: e.payer_name || null,
    eventId: e.event_id ?? null,
    channel: e.channel || null,
    days: num(e.days),
    detail: e.detail || null,
  };
}
